import React, { useEffect, useState } from 'react';
import firebase from '../db/firebase/firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc
} from 'firebase/firestore';
import { Trash2 } from 'lucide-react';

const PurchasePoints = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const q = query(collection(firebase.db, 'requestPoint'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, snapshot => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setRequests(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleApprove = async (id) => {
    const confirmed = window.confirm("Are you sure you want to approve this request? This action cannot be undone.");
    if (!confirmed) return;

    const docRef = doc(firebase.db, 'requestPoint', id);
    await updateDoc(docRef, { state: 'approved', rejectReason: '' });
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Enter the reason for rejection:");
    if (!reason) {
      alert("You must provide a reason to reject.");
      return;
    }

    const confirmed = window.confirm("Are you sure you want to reject this request? This action cannot be undone.");
    if (!confirmed) return;

    const docRef = doc(firebase.db, 'requestPoint', id);
    await updateDoc(docRef, { state: 'rejected', rejectReason: reason });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this request? This action cannot be undone.");
    if (!confirmed) return;

    const docRef = doc(firebase.db, 'requestPoint', id);
    await deleteDoc(docRef);
  };

  const getStateColor = (state) => {
    if (state === 'approved') return 'text-green-600';
    if (state === 'pending') return 'text-yellow-600';
    if (state === 'rejected') return 'text-red-600';
    return 'text-gray-500';
  };

  const sortedRequests = [...requests].sort((a, b) => {
    if (a.state === 'pending' && b.state !== 'pending') return -1;
    if (a.state !== 'pending' && b.state === 'pending') return 1;
    return b.createdAt?.seconds - a.createdAt?.seconds;
  });

  const filteredRequests = sortedRequests.filter((req) => {
    if (filter === "all") return true;
    return req.state === filter;
  });

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-white z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-gray-800"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex-1 overflow-y-auto p-6 bg-gray-300">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Purchase Points</h1>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="p-2 border border-gray-300 rounded-md bg-white shadow-sm"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="flex flex-1 flex-wrap gap-4">
        {filteredRequests.length === 0 ? (
          <p className="text-center w-full text-gray-500 text-lg">No requests found.</p>
        ) : (
          filteredRequests.map(({ id, email, emailContact, points, total, state, rejectReason, createdAt }) => (
            <div
              key={id}
              className="min-w-[300px] relative flex-1 bg-white shadow-md rounded-lg p-4 flex flex-col justify-between"
            >
              {/* 🔹 Tacho rojo arriba a la derecha */}
              <Trash2
                size={20}
                className="text-red-500 hover:text-red-700 cursor-pointer absolute top-3 right-3"
                onClick={() => handleDelete(id)}
              />

              <div>
                <p><span className="font-semibold">Email:</span> {email}</p>
                <p><span className="font-semibold">Email Contact:</span> {emailContact}</p>
                <p><span className="font-semibold">Points:</span> {points}</p>
                <p><span className="font-semibold">Total (£):</span> £{total}</p>
                <p>
                  <span className="font-semibold">State:</span>{" "}
                  <span className={getStateColor(state)}>{state}</span>
                </p>
                {state === 'rejected' && rejectReason && (
                  <p className="text-red-600 mt-1"><span className="font-semibold">Reason:</span> {rejectReason}</p>
                )}
              </div>

              <div className="flex justify-between items-center mt-4">
                <p className="text-sm text-gray-400">
                  {createdAt?.toDate ? createdAt.toDate().toLocaleDateString() : '—'}
                </p>

                {state === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(id)}
                      className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(id)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PurchasePoints;